import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../theme';
import { supabase } from '../lib/supabase';

function Signup() {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Create auth user WITH first_name in metadata
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: email,
        password: password,
        options: {
          data: {
            first_name: firstName,
          }
        }
      });

      if (authError) throw authError;

      // Create user profile
      const { error: profileError } = await supabase
        .from('user_profiles')
        .insert([
          {
            user_id: authData.user.id,
            first_name: firstName,
            email: email,
            is_trial_user: true,
            trial_start_date: new Date().toISOString(),
            subscription_status: 'trial',
          },
        ]);

      if (profileError) throw profileError;

      // Create trial subscription row with 30-day trial
      const trialEndsAt = new Date();
      trialEndsAt.setDate(trialEndsAt.getDate() + 10);

      await supabase
        .from('subscriptions')
        .insert([{
          user_id: authData.user.id,
          subscription_status: 'trial',
          trial_ends_at: trialEndsAt.toISOString(),
        }]);

      // Send welcome email
      try {
        await fetch('/api/send-welcome-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, name: firstName }),
        });
      } catch (emailError) {
        // Don't block signup if email fails
        console.error('Welcome email failed:', emailError);
      }

      // Success! Navigate to dashboard
      navigate('/dashboard');
    } catch (error) {
      setError(error.message || 'Failed to create account');
      console.error('Signup error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>My Greatest Self</h1>
        <p style={styles.subtitle}>Start your transformation</p>
        
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>First Name</label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              style={styles.input}
              placeholder="Scott"
              required
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              placeholder="you@example.com"
              required
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              placeholder="••••••••"
              required
              minLength="8"
            />
            <p style={styles.hint}>Minimum 8 characters</p>
          </div>

          {error && <div style={styles.error}>{error}</div>}

          <button type="submit" style={styles.button} disabled={loading}>
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <p style={styles.footer}>
          Already have an account?{' '}
          <span 
            style={styles.link} 
            onClick={() => navigate('/login')}
          >
            Sign in
          </span>
        </p>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.bgDark,
    padding: '20px',
  },
  card: {
    backgroundColor: theme.colors.bgMedium,
    borderRadius: '12px',
    padding: '40px',
    width: '100%',
    maxWidth: '400px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
  },
  title: {
    fontSize: '2rem',
    fontWeight: '700',
    background: theme.colors.gradient,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: '8px',
    textAlign: 'center',
  },
  subtitle: {
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginBottom: '32px',
    fontSize: '1rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    color: theme.colors.textSecondary,
    fontSize: '0.875rem',
    fontWeight: '500',
  },
  input: {
    backgroundColor: theme.colors.bgLight,
    border: `1px solid ${theme.colors.bgLight}`,
    borderRadius: '8px',
    padding: '0.75em 1em',
    color: theme.colors.textPrimary,
    fontSize: '1rem',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  hint: {
    color: theme.colors.textMuted,
    fontSize: '0.75rem',
    margin: '0',
  },
  button: {
    background: theme.colors.gradient,
    border: 'none',
    borderRadius: '8px',
    padding: '0.875em',
    color: 'white',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
    transition: 'opacity 0.2s',
  },
  error: {
    backgroundColor: '#dc2626',
    color: 'white',
    padding: '12px',
    borderRadius: '8px',
    fontSize: '0.875rem',
  },
  footer: {
    textAlign: 'center',
    marginTop: '24px',
    color: theme.colors.textMuted,
    fontSize: '0.875rem',
  },
  link: {
    color: theme.colors.teal,
    cursor: 'pointer',
    fontWeight: '600',
  },
};

export default Signup;